import { useState, useEffect, useCallback, useRef } from "react";
import useVoiceConversation from "@/hooks/useVoiceConversation";
import WorkflowSteps from "../components/WorkflowSteps";

import Step1Form from "../components/Step1";
import Step2Form from "../components/Step2";
import Step3Form from "../components/Step4";
import Step4Form from "../components/Step3";

import AIAssistant from "../components/AIAssistant";
import { useRealtimeContext } from "../../context/RealtimeContext";
import { getCurrentDemoConfig } from "../../config/demoSites";
import useFormManager from "../../hooks/useFormsManager";

export default function SNSWDemoLayout() {
    const [currentStep, setCurrentStep] = useState(1);
    const { groundingFiles, latestTranscript } = useVoiceConversation();
    const { sessionContext, updateSessionContext } = useRealtimeContext();
    const prevStepRef = useRef(currentStep);

    // Initialize the form manager
    useFormManager();

    // Update both local state and session context when step changes
    const updateStep = useCallback(
        async (newStep: number) => {
            // Get the step configuration for the new step
            const demoConfig = getCurrentDemoConfig();
            const stepConfig = demoConfig.steps.find(s => s.id === newStep);

            if (!stepConfig) {
                console.error(`Step ${newStep} not found in configuration`);
                return;
            }

            // Update local UI state immediately
            setCurrentStep(newStep);

            // Update context with the new step and prompt
            updateSessionContext(prev => {
                const updated = structuredClone(prev);

                // Update the step
                updated.layout.workflow.currentStep = newStep;

                // Update the prompt if needed
                if (stepConfig.promptStep) {
                    updated.layout.assistant.lastPrompt = stepConfig.promptStep;
                }

                return updated;
            }, "step-change");

            // Handle backend update asynchronously
            if (stepConfig.promptStep && stepConfig.promptStep !== sessionContext.layout.assistant.lastPrompt) {
                try {
                    const response = await fetch(`/updateprompt?step=${stepConfig.promptStep}`);

                    if (!response.ok) {
                        console.error(`[PROMPT_SYNC] Failed to update prompt (${response.status})`);
                    } else {
                        console.log(`[PROMPT_SYNC] Successfully updated prompt to ${stepConfig.promptStep}`);
                    }
                } catch (error) {
                    console.error("[PROMPT_SYNC] Error updating prompt:", error);
                }
            }
        },
        [sessionContext, updateSessionContext]
    );

    // Navigation functions
    function handleNextStep() {
        // Assuming 4 steps total, update as needed for SNSW
        if (currentStep < 4) updateStep(currentStep + 1);
    }

    function handlePrevStep() {
        if (currentStep > 1) updateStep(currentStep - 1);
    }

    // For direct step selection from WorkflowSteps
    function handleStepChange(stepNumber: number) {
        updateStep(stepNumber);
    }

    // Synchronize latestTranscript from useVoiceConversation with sessionContext
    useEffect(() => {
        updateSessionContext(prev => {
            const updated = structuredClone(prev);
            if (updated.layout.workflow.steps[currentStep]) {
                updated.layout.workflow.steps[currentStep].chat.latestTranscript = latestTranscript;
            }
            return updated;
        }, "transcript-update");
    }, [latestTranscript, currentStep, updateSessionContext]);

    // Track step changes for debugging
    useEffect(() => {
        if (currentStep !== prevStepRef.current) {
            console.log(`[STEP_CHANGE] ${prevStepRef.current} → ${currentStep} (local state updated)`);
            prevStepRef.current = currentStep;
        }
    }, [currentStep]);

    // Keep UI in sync with context if changed externally
    useEffect(() => {
        if (sessionContext.layout.workflow.currentStep !== currentStep) {
            setCurrentStep(sessionContext.layout.workflow.currentStep);
        }
    }, [sessionContext.layout.workflow.currentStep, currentStep]);

    return (
        <>
            <div className="flex h-[calc(100vh-80px)] flex-row text-gray-900">
                {/* LEFT PANEL - Split into fixed header and scrollable content */}
                <div className="flex w-2/3 flex-col">
                    {/* Fixed header area for steps */}
                    <div className="sticky top-0 z-10 bg-white px-8 pb-4 pt-8">
                        <WorkflowSteps currentStep={currentStep} onStepChange={handleStepChange} />
                    </div>

                    {/* Scrollable content area */}
                    <div className="flex flex-col overflow-y-auto px-8 pb-8">
                        <div className="rounded-md bg-white p-6 shadow-md">
                            {currentStep === 1 && (
                                <Step1Form currentStep={currentStep} sessionContext={sessionContext} updateSessionContext={updateSessionContext} />
                            )}
                            {currentStep === 2 && (
                                <Step2Form currentStep={currentStep} sessionContext={sessionContext} updateSessionContext={updateSessionContext} />
                            )}
                            {currentStep === 3 && (
                                <Step3Form currentStep={currentStep} sessionContext={sessionContext} updateSessionContext={updateSessionContext} />
                            )}
                            {currentStep === 4 && (
                                <Step4Form currentStep={currentStep} sessionContext={sessionContext} updateSessionContext={updateSessionContext} />
                            )}
                        </div>

                        {/* Display grounding files if any */}
                        {groundingFiles && groundingFiles.length > 0 && (
                            <div className="mt-4 rounded-md bg-white p-4 shadow-md">
                                <h4 className="font-medium text-gray-700">Additional Resources</h4>
                                <ul className="mt-2">
                                    {groundingFiles.map(file => (
                                        <li key={file.id} className="text-blue-600 hover:text-blue-800">
                                            <a href={file.url ?? "#"} target="_blank" rel="noopener noreferrer">
                                                {file.name}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Navigation buttons */}
                        <div className="mt-4 flex justify-between">
                            <button
                                onClick={handlePrevStep}
                                disabled={currentStep <= 1}
                                className="rounded border border-gray-400 px-4 py-2 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleNextStep}
                                disabled={currentStep >= 4}
                                className="rounded bg-primary px-4 py-2 font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL - AI Assistant */}
                <div className="flex h-full w-1/3 flex-col overflow-hidden border-l border-gray-200 bg-white">
                    <div className="flex min-h-0 flex-grow flex-col overflow-hidden p-6">
                        <AIAssistant currentStep={currentStep} sessionContext={sessionContext} updateSessionContext={updateSessionContext} />
                    </div>
                </div>
            </div>
            {/* Footer */}
            <footer className="fixed bottom-0 left-0 z-[9999] flex w-full flex-col items-center justify-center border-t border-gray-200 bg-white px-4 py-2 shadow-md">
                <img src="/unhcr-logo.png" className="mb-0.5 h-8" alt="UNHCR Logo" />
                <small className="my-0.5 block text-xs text-gray-600">Powered by Enterprise AI</small>
                <a
                    href="mailto:growth@enterpriseaigroup.com"
                    className="inline-block rounded bg-blue-600 px-3 py-1 text-xs font-bold leading-tight text-white transition-colors hover:bg-blue-700 hover:shadow-md"
                >
                    Learn more about EnterpriseAI
                </a>
            </footer>
        </>
    );
}
